import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Container from "../../components/Container/Container";
import { useAppContext } from "../../context/AppContext";
import { renderTableByType } from "../../components/Tables/tableRegistry";
import { CollectedAnswer, OnAnswersCollected } from "../../types/training";
import { useSectionCompletion } from "../../hooks/useSectionCompletion";
import {
  AnchorContentItem,
  ImageContentItem,
  ListContentValue,
  PageContent,
  SectionPage,
} from "../../types/content";
import "./section.scss";

interface SectionProps {
  topic_image: string;
  title: string;
  sectionId: string;
  content: SectionPage[];
  nextRoute: string;
  onAnswersCollected: OnAnswersCollected;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  onBack: () => void;
  onComplete: () => void;
  isCompleted: boolean;
  isTop?: boolean;
  sectionId: string;
  onAnswersCollected: OnAnswersCollected;
}

interface QuestionnaireSection {
  sectionId: string;
  questions?: unknown[];
}

const renderMarkdownText = (text: string) => (
  <ReactMarkdown className="section__paragraph" components={{ div: "span", p: "span" }}>
    {text}
  </ReactMarkdown>
);

const getPlainText = (text: PageContent["text"]) => (typeof text === "string" ? text : "");

const isImageContentItem = (value: ListContentValue): value is ImageContentItem =>
  typeof value === "object" && !Array.isArray(value) && value.type === "img";

const isAnchorContentItem = (value: ListContentValue): value is AnchorContentItem =>
  typeof value === "object" && !Array.isArray(value) && value.type === "anchor";

const renderListContentItem = (
  item: PageContent,
  textItem: ListContentValue,
  textIndex: number,
  selectedProduct: string,
  sectionId: string
) => {
  if (isImageContentItem(textItem)) {
    return (
      <div key={textIndex} className={`section__image-container ${item.class || ""}`}>
        <img
          src={`/assets/${selectedProduct}/${sectionId}/${textItem.text}`}
          alt="#"
          className="section__image"
        />
      </div>
    );
  }

  if (isAnchorContentItem(textItem)) {
    return (
      <div key={textIndex}>
        <a
          href={textItem.url || textItem.text}
          className={`section__anchor ${textItem.class || ""}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {textItem.label || textItem.text}
        </a>
      </div>
    );
  }

  if (Array.isArray(textItem)) {
    return (
      <ul className="section__list unordered" key={textIndex}>
        {textItem.map((nestedItem, nestedIndex) => (
          <li className="section__list-item" key={nestedIndex}>
            {renderMarkdownText(nestedItem)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <li className={`section__list-item ${item.list_item_class || ""}`} key={textIndex}>
      {renderMarkdownText(textItem)}
    </li>
  );
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  onBack,
  onComplete,
  isCompleted,
  isTop = false,
  sectionId,
  onAnswersCollected,
}) => {
  const { questionnaire_material, selectedProduct, isEnglish } = useAppContext();
  const navigate = useNavigate();

  const questionnaireSection = questionnaire_material.content.find(
    (item: QuestionnaireSection) => item.sectionId === sectionId
  );

  const hasQuestions = Boolean(questionnaireSection?.questions?.length);

  return (
    <div className={`section__pagination${isTop ? " section__pagination--top" : ""}`}>
      <button
        className="section__pagination-back-button"
        onClick={currentPage === 1 ? onBack : onPrevious}
      >
        <img
          className="section__pagination-back-button-arrow"
          src="/assets/arrow-left.svg"
          alt="chevron pointing left"
        />
        {isEnglish ? "Go back" : "Grižti atgal"}
      </button>
      <div className="section__pagination-dots">
        {Array.from({ length: totalPages }).map((_, index) => (
          <span
            key={index}
            className={`section__pagination-dot ${currentPage === index + 1 ? "active" : ""}`}
          />
        ))}
      </div>
      {hasQuestions ? (
        <button
          className="section__pagination-next-button"
          onClick={currentPage === totalPages ? onComplete : onNext}
          disabled={currentPage === totalPages && isCompleted}
        >
          {currentPage === totalPages
            ? isEnglish
              ? "Take the test"
              : "Spręsti testą"
            : isEnglish
              ? "Next"
              : "Toliau"}
          <img
            className="section__pagination-back-button-arrow"
            src="/assets/arrow-right.svg"
            alt="arrow pointing right"
          />
        </button>
      ) : (
        <button
          className="section__pagination-next-button"
          onClick={() => {
            onAnswersCollected([] as CollectedAnswer[]);
            navigate(`/${selectedProduct}/dashboard`);
          }}
          disabled={isCompleted}
        >
          {isEnglish ? "Complete" : "Baigti"}
          <img
            className="section__pagination-back-button-arrow"
            src="/assets/arrow-right.svg"
            alt="arrow pointing right"
          />
        </button>
      )}
    </div>
  );
};

// Helper component for rendering each content item
const SectionContentItem: React.FC<{
  item: PageContent;
  selectedProduct: string;
  sectionId: string;
}> = ({ item, selectedProduct, sectionId }) => {
  const renderedTable = renderTableByType(item.type);

  if (renderedTable) {
    return renderedTable;
  }

  switch (item.type) {
    case "list":
      return (
        <ul className={`section__list ${item.list_type} ${item.class || ""}`}>
          {Array.isArray(item.text) &&
            item.text.map((textItem, textIndex) =>
              renderListContentItem(item, textItem, textIndex, selectedProduct, sectionId)
            )}
        </ul>
      );
    case "heading":
      return (
        <h2 className={`section__sub-heading ${item.class || ""}`}>{getPlainText(item.text)}</h2>
      );
    case "divider":
      return <hr className={`section__divider ${item.class || ""}`} />;
    case "floating-heading":
      return (
        <div className={`section__floating-heading ${item.class || ""}`}>
          <h2 className="section__floating-heading__text">{getPlainText(item.text)}</h2>
        </div>
      );
    case "main-heading":
      return (
        <h2 className={`section__heading section__heading--title ${item.class || ""}`}>
          {getPlainText(item.text)}
        </h2>
      );
    case "section-heading":
      return <h2 className={`section__heading ${item.class || ""}`}>{getPlainText(item.text)}</h2>;
    case "anchor":
      return (
        <a
          href={getPlainText(item.text)}
          className={`section__anchor ${item.class || ""}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {getPlainText(item.text)}
        </a>
      );
    case "img":
      return (
        <div className={`section__image-container ${item.class || ""}`}>
          <img
            src={`/assets/${selectedProduct}/${sectionId}/${getPlainText(item.text)}`}
            alt="#"
            className="section__image"
          />
        </div>
      );
    default:
      return (
        <ReactMarkdown
          className={`section__paragraph ${item.class || ""}`}
          components={{ div: "span", p: "span" }}
        >
          {typeof item.text === "string"
            ? item.text
            : Array.isArray(item.text)
              ? item.text.join(" ")
              : ""}
        </ReactMarkdown>
      );
  }
};

const Section: React.FC<SectionProps> = React.memo(
  ({ topic_image, title, sectionId, content, nextRoute, onAnswersCollected }) => {
    const navigate = useNavigate();
    const { selectedProduct } = useAppContext();
    const isCompleted = useSectionCompletion(selectedProduct);
    const [currentPage, setCurrentPage] = useState(1);

    const handleNextPage = () => {
      if (currentPage < content.length) {
        setCurrentPage(currentPage + 1);
      }
    };

    const handlePreviousPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const handleBack = () => {
      const path = window.location.pathname;
      if (path.startsWith("/questionnaire")) {
        const sectionNumber = path.replace("/questionnaire", "");
        navigate(`/${selectedProduct}/section${sectionNumber}`);
      } else if (path.startsWith("/section")) {
        navigate(`/${selectedProduct}/dashboard`);
      } else {
        navigate(-1);
      }
    };

    const handleComplete = () => {
      navigate(`/${selectedProduct}${nextRoute}`);
    };

    const currentPageContent = useMemo(() => {
      return content.find((page) => page.page === currentPage)?.page_content || [];
    }, [content, currentPage]);

    const paginationProps: PaginationProps = {
      currentPage,
      totalPages: content.length,
      onPrevious: handlePreviousPage,
      onNext: handleNextPage,
      onBack: handleBack,
      onComplete: handleComplete,
      isCompleted,
      onAnswersCollected,
      sectionId,
    };

    return (
      <Container>
        <div className={"section"}>
          <Pagination {...paginationProps} isTop />
          <div className="section__content">
            {currentPageContent.map((item, index) => (
              <SectionContentItem
                key={index}
                item={item}
                selectedProduct={selectedProduct}
                sectionId={sectionId}
              />
            ))}
          </div>
          <Pagination {...paginationProps} />
        </div>
      </Container>
    );
  }
);

export default Section;
